// Teacher Check token scope (Teacher Loop, contract §4 "token-scoped read
// access").
//
// While — and only while — the Teacher Check page (/check/:token) is mounted,
// the submission reads it makes for that ONE submission carry
// `X-Teacher-Check-Token: <token>`. The backend treats the header like a share
// link for that submission (read only, while the check is live), which lets the
// page reuse the existing public renderer without making the draft public.
//
// Containment:
//   - The scope is set by the page on mount and released on unmount; releasing
//     an older scope never clears a newer one.
//   - Only `${API}/submissions/<that id>` and paths under it get the header —
//     never another submission, never any other endpoint.
//   - With the build flag off nothing can set a scope, and headersFor returns
//     the same empty object every time, so service.js sends exactly what it
//     sent before.

// Same test as utils/teacherLoop.isTeacherLoopEnabled, inlined so service.js
// (which imports this file) gains no new module dependencies.
const isTeacherLoopEnabled = () => process.env.REACT_APP_TEACHER_LOOP === 'on';

export const TEACHER_CHECK_TOKEN_HEADER = 'X-Teacher-Check-Token';

const NO_HEADERS = Object.freeze({});

let activeScope = null;

const apiBase = () =>
  (process.env.REACT_APP_API_BASE_URL ?? 'http://localhost:8080').replace(
    /\/+$/,
    ''
  );

/**
 * Starts sending the token on that submission's reads. Returns a release
 * function; call it on unmount.
 */
export const setTeacherCheckScope = ({ token, submissionId }) => {
  if (!isTeacherLoopEnabled() || !token || !submissionId) {
    return () => {};
  }
  const scope = Object.freeze({
    token: String(token),
    submissionId: String(submissionId),
  });
  activeScope = scope;
  return () => {
    if (activeScope === scope) activeScope = null;
  };
};

export const clearTeacherCheckScope = () => {
  activeScope = null;
};

export const getTeacherCheckScope = () => activeScope;

const startsSubmissionPath = (url, prefix) => {
  if (!url.startsWith(prefix)) return false;
  const next = url.charAt(prefix.length);
  return next === '' || next === '/' || next === '?';
};

/**
 * Extra headers for one API request: the token for the scoped submission's
 * reads, otherwise the shared empty object.
 */
export const teacherCheckHeadersFor = (url) => {
  const scope = activeScope;
  if (!scope || typeof url !== 'string' || !isTeacherLoopEnabled()) {
    return NO_HEADERS;
  }
  const base = `${apiBase()}/submissions/`;
  const ids = [scope.submissionId, encodeURIComponent(scope.submissionId)];
  const matches = ids.some((id) => startsSubmissionPath(url, `${base}${id}`));
  return matches ? { [TEACHER_CHECK_TOKEN_HEADER]: scope.token } : NO_HEADERS;
};
